import type { OnboardingVersion } from '@modules/firebase-core/converters/onboardingConverter';
import type { StepNode } from '@segmently/schema-models/v2';

export interface OnboardingV2JsonDocument {
  schemaVersion: string;
  defaultLanguage: string;
  screens: Record<string, StepNode>;
  languages: string[];
  launchScreenId: string;
  variables: Record<string, unknown>;
  metadata: Record<string, unknown>;
  /** Flow-level animation defaults (schema 2.17) — present only when the version carries them */
  defaultTransition?: Record<string, unknown>;
  defaultScreenAnimation?: Record<string, unknown>;
  /** Per-StepKind transition defaults (local FlowDocument extension, theme Calm Paywall) */
  perKindTransitions?: Record<string, unknown>;
}

export interface OnboardingV2ExportMetadata {
  projectId: string;
  onboardingId: string;
  versionId: string;
  versionName: string;
  exportedAt: string;
  format: 'v2';
}

export interface PreparedOnboardingV2ImportData {
  screens: Record<string, unknown>;
  schemaVersion: string;
  defaultLanguage: string;
  languages: string[];
  launchScreenId: string;
  metadata: Record<string, unknown>;
  importedVariables: Record<string, unknown>;
  referenceIssues: FlowDocumentReferenceIssue[];
  referenceWarning: string | null;
  /** Flow-level animation defaults round-tripped from the export (absent = not configured) */
  defaultTransition?: Record<string, unknown>;
  defaultScreenAnimation?: Record<string, unknown>;
  perKindTransitions?: Record<string, unknown>;
}

export function normalizeImportedVariables(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return value as Record<string, unknown>;
}

export function buildOnboardingV2JsonDocument(params: {
  selectedVersion: OnboardingVersion;
  selectedVersionId: string;
  projectId: string;
  onboardingId: string;
  screens: StepNode[];
  exportedAt?: string;
}): OnboardingV2JsonDocument {
  const { selectedVersion, selectedVersionId, projectId, onboardingId, screens } = params;
  const screensMap: Record<string, StepNode> = {};

  screens.forEach((step) => {
    screensMap[step.id] = step;
  });

  // Flow-level animation defaults (schema 2.17 + perKindTransitions local
  // extension) — dropping them from the export loses the screen-to-screen
  // transition on every export→import / CLI funnels-apply round-trip.
  const graph = selectedVersion.screensGraph as Record<string, unknown> | undefined;
  const defaultTransition = isRecord(graph?.defaultTransition) ? graph.defaultTransition : undefined;
  const defaultScreenAnimation = isRecord(graph?.defaultScreenAnimation) ? graph.defaultScreenAnimation : undefined;
  const perKindTransitions = isRecord(graph?.perKindTransitions) ? graph.perKindTransitions : undefined;

  return {
    schemaVersion: '2.0.0',
    defaultLanguage: selectedVersion.screensGraph?.defaultLanguage || 'en-US',
    screens: screensMap,
    languages: selectedVersion.screensGraph?.languages || ['en-US'],
    launchScreenId:
      selectedVersion.screensGraph?.launchScreenId ||
      (screens.length > 0 ? screens[0].id : ''),
    variables: normalizeImportedVariables(selectedVersion.variables),
    ...(defaultTransition ? { defaultTransition } : {}),
    ...(defaultScreenAnimation ? { defaultScreenAnimation } : {}),
    ...(perKindTransitions ? { perKindTransitions } : {}),
    metadata: {
      projectId,
      onboardingId,
      versionId: selectedVersionId,
      versionName: selectedVersion.name,
      exportedAt: params.exportedAt || new Date().toISOString(),
      format: 'v2',
    } satisfies OnboardingV2ExportMetadata,
  };
}

/** The two version writes an import makes (OnboardingService implements both). */
export interface ImportedVersionWriter {
  updateVersion(
    projectId: string,
    onboardingId: string,
    versionId: string,
    updates: Partial<OnboardingVersion>,
  ): Promise<void>;
  updateVersionAnimationDefaults(
    projectId: string,
    onboardingId: string,
    versionId: string,
    defaults: {
      defaultTransition?: Record<string, unknown> | null;
      defaultScreenAnimation?: Record<string, unknown> | null;
      perKindTransitions?: Record<string, unknown> | null;
    },
  ): Promise<void>;
}

/**
 * Write an imported FlowDocument's version-level data (screensGraph metadata, variables and the
 * flow-level motion defaults) onto the target version.
 */
export async function writeImportedVersionGraph(
  writer: ImportedVersionWriter,
  target: { projectId: string; onboardingId: string; versionId: string },
  importData: PreparedOnboardingV2ImportData,
): Promise<void> {
  const { projectId, onboardingId, versionId } = target;
  // screensGraph metadata. `updateVersion` is a merge write (`setDoc(…, { merge: true })`): nested
  // keys the payload omits are KEPT and nested maps are merged, so it does not carry the motion defaults.
  await writer.updateVersion(projectId, onboardingId, versionId, {
    screensGraph: {
      launchScreenId: importData.launchScreenId,
      defaultLanguage: importData.defaultLanguage,
      languages: importData.languages,
      screens: {},
      schemaVersion: importData.schemaVersion,
      metadata: importData.metadata,
    },
    variables: importData.importedVariables,
  });
  // Motion defaults: each replaced whole by the document's own, or deleted when the document has
  // none (null → deleteField) — never left over from the previous funnel (final review M-5).
  await writer.updateVersionAnimationDefaults(projectId, onboardingId, versionId, {
    defaultTransition: importData.defaultTransition ?? null,
    defaultScreenAnimation: importData.defaultScreenAnimation ?? null,
    perKindTransitions: importData.perKindTransitions ?? null,
  });
}

type TraversalPath = Array<string | number>;

export interface FlowDocumentReferenceIssue {
  type: 'missing-variable' | 'missing-enum-option';
  variableId: string;
  optionId?: string;
  path: string;
}

function pathToString(path: TraversalPath): string {
  return path
    .map((part) => (typeof part === 'number' ? `[${part}]` : part))
    .join('.');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function extractRouteGuardValues(value: unknown): string[] {
  if (typeof value !== 'string' || value.length === 0) {
    return [];
  }

  return value.split(',').map((part) => part.trim()).filter(Boolean);
}

function collectRouteGuardIssues(params: {
  value: unknown;
  path: TraversalPath;
  variables: Record<string, unknown>;
  issues: FlowDocumentReferenceIssue[];
}): void {
  const { value, path, variables, issues } = params;

  if (Array.isArray(value)) {
    value.forEach((item, index) => collectRouteGuardIssues({
      value: item,
      path: [...path, index],
      variables,
      issues,
    }));
    return;
  }

  if (!isRecord(value)) {
    return;
  }

  if (
    typeof value.key === 'string' &&
    typeof value.operator === 'string' &&
    Object.prototype.hasOwnProperty.call(value, 'value')
  ) {
    const variable = variables[value.key];
    if (!variable) {
      issues.push({
        type: 'missing-variable',
        variableId: value.key,
        path: pathToString(path),
      });
    } else if (isRecord(variable) && (variable.type === 'enum' || variable.type === 'enum[]')) {
      const optionIds = Array.isArray(variable.options)
        ? new Set(
          variable.options
            .filter(isRecord)
            .map((option) => option.id)
            .filter((id): id is string => typeof id === 'string')
        )
        : new Set<string>();

      for (const optionId of extractRouteGuardValues(value.value)) {
        if (!optionIds.has(optionId)) {
          issues.push({
            type: 'missing-enum-option',
            variableId: value.key,
            optionId,
            path: pathToString(path),
          });
        }
      }
    }
  }

  Object.entries(value).forEach(([key, child]) => collectRouteGuardIssues({
    value: child,
    path: [...path, key],
    variables,
    issues,
  }));
}

export function validateFlowDocumentVariableReferences(params: {
  screens: Record<string, unknown>;
  variables: Record<string, unknown>;
}): FlowDocumentReferenceIssue[] {
  const issues: FlowDocumentReferenceIssue[] = [];

  collectRouteGuardIssues({
    value: params.screens,
    path: ['screens'],
    variables: params.variables,
    issues,
  });

  return issues;
}

export function formatFlowDocumentReferenceIssues(issues: FlowDocumentReferenceIssue[]): string {
  return issues.map((issue) => {
    if (issue.type === 'missing-enum-option') {
      return `Missing enum option "${issue.optionId}" for variable "${issue.variableId}" at ${issue.path}`;
    }

    return `Missing variable "${issue.variableId}" at ${issue.path}`;
  }).join('; ');
}

export function buildFlowDocumentReferenceWarning(issues: FlowDocumentReferenceIssue[]): string | null {
  if (issues.length === 0) {
    return null;
  }

  const plural = issues.length === 1 ? '' : 's';
  const missingVariables = Array.from(new Set(
    issues
      .filter((issue) => issue.type === 'missing-variable')
      .map((issue) => issue.variableId)
  ));
  const missingOptionsByVariable = new Map<string, string[]>();

  issues.forEach((issue) => {
    if (issue.type !== 'missing-enum-option' || !issue.optionId) {
      return;
    }

    const values = missingOptionsByVariable.get(issue.variableId) || [];
    if (!values.includes(issue.optionId)) {
      values.push(issue.optionId);
    }
    missingOptionsByVariable.set(issue.variableId, values);
  });

  const details: string[] = [];
  if (missingVariables.length > 0) {
    details.push(`missing variables: ${missingVariables.join(', ')}`);
  }
  missingOptionsByVariable.forEach((optionIds, variableId) => {
    details.push(`missing enum options for ${variableId}: ${optionIds.join(', ')}`);
  });

  const detailsText = details.length > 0 ? ` ${details.join('; ')}.` : '';
  return `Imported with ${issues.length} unresolved condition reference${plural}.${detailsText} Review affected edge conditions before publishing.`;
}

export function prepareOnboardingV2ImportData(data: unknown): PreparedOnboardingV2ImportData {
  if (!isRecord(data) || !isRecord(data.screens)) {
    throw new Error('Invalid JSON format: missing screens object');
  }

  const schemaVersion = typeof data.schemaVersion === 'string' ? data.schemaVersion : '';
  if (!schemaVersion.startsWith('2.')) {
    throw new Error(
      'Only V2 FlowDocument imports are supported. Convert legacy V1 exports before importing.'
    );
  }

  const importedVariables = normalizeImportedVariables(data.variables);
  const referenceIssues = validateFlowDocumentVariableReferences({
    screens: data.screens,
    variables: importedVariables,
  });

  const screenIds = Object.keys(data.screens);
  const languages = Array.isArray(data.languages)
    ? data.languages.filter((value): value is string => typeof value === 'string')
    : [];

  return {
    screens: data.screens,
    schemaVersion,
    defaultLanguage: typeof data.defaultLanguage === 'string' ? data.defaultLanguage : 'en-US',
    languages: languages.length > 0 ? languages : ['en-US'],
    launchScreenId: typeof data.launchScreenId === 'string' ? data.launchScreenId : screenIds[0] || '',
    metadata: isRecord(data.metadata) ? data.metadata : {},
    importedVariables,
    referenceIssues,
    referenceWarning: buildFlowDocumentReferenceWarning(referenceIssues),
    ...(isRecord(data.defaultTransition) ? { defaultTransition: data.defaultTransition } : {}),
    ...(isRecord(data.defaultScreenAnimation) ? { defaultScreenAnimation: data.defaultScreenAnimation } : {}),
    ...(isRecord(data.perKindTransitions) ? { perKindTransitions: data.perKindTransitions } : {}),
  };
}
